import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { updateProfile } from '../api/auth';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { MailMark, PhoneMark, TelegramMark } from '../components/brandIcons';
import { ProfileRichFooter } from '../components/ProfileRichFooter';
import { formatLocaleTag } from '../i18n';

const IDENTITY_FIELDS = ['given_name', 'family_name', 'date_of_birth', 'gender'] as const;
type IdentityField = (typeof IDENTITY_FIELDS)[number];



function errorDetail(err: unknown): string | null {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== 'object' || !('detail' in err.body)) return null;
  const detail = (err.body as { detail: unknown }).detail;
  return typeof detail === 'string' ? detail : null;
}

export function ProfilePage() {
  const { t, i18n } = useTranslation(['profile', 'common']);
  const { user, loading, applyUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const locale = (i18n.resolvedLanguage || i18n.language || 'az').split('-')[0];
  const msg = {
    title: t('title'), info: t('info'), idCopy: t('idCopy'), copied: t('copied'), copyFailed: t('copyFailed'),
    name: t('name'), surname: t('surname'), birth: t('birth'), gender: t('gender'), chooseGender: t('chooseGender'),
    male: t('male'), female: t('female'), save: t('save'), saved: t('saved'), missing: t('missing'), failed: t('failed'),
    alreadySaved: t('alreadySaved'), confirmTitle: t('confirmTitle'), confirmText: t('confirmText'), verify: t('verify'),
    back: t('back'), contacts: t('contacts'), linked: t('linked'), notLinked: t('notLinked'), unverified: t('unverified'),
    manage: t('manage'), telegram: t('telegram'), unavailable: t('unavailable'), phone: t('phone'), email: t('email'),
    change: t('change'), pending: t('pending'), requestTitle: t('requestTitle'), changeHelp: t('changeHelp'),
    reason: t('reason'), submit: t('submit'), noChanges: t('noChanges'), changeFailed: t('changeFailed'),
    changeSent: t('changeSent'), support: t('support'), age: t('age'), loggingOut: t('loggingOut'), logout: t('logout'),
  };
  const [copyToast, setCopyToast] = useState<'success' | 'error' | null>(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const copyTimerRef = useRef<number | null>(null);
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');


  const locked = useMemo(() => {
    const fromServer = user?.identity_locked_fields || [];
    if (fromServer.length) return new Set<string>(fromServer);
    const inferred = new Set<string>();
    if (user?.given_name) inferred.add('given_name');
    if (user?.family_name) inferred.add('family_name');
    if (user?.date_of_birth) inferred.add('date_of_birth');
    if (user?.gender) inferred.add('gender');
    return inferred;
  }, [user]);
  const allLocked = IDENTITY_FIELDS.every((field) => locked.has(field));
  const anyUnlocked = !allLocked;

  useEffect(() => {
    if (!user) return;
    setGivenName(user.given_name || '');
    setFamilyName(user.family_name || '');
    setDob(user.date_of_birth || '');
    setGender(user.gender || '');
  }, [user?.public_id, user?.given_name, user?.family_name, user?.date_of_birth, user?.gender]);

  useEffect(() => () => {
    if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
  }, []);

  async function copyId() {
    if (!user?.public_id || copyBusy) return;
    setCopyBusy(true);
    setCopyToast(null);
    if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
    try {
      await navigator.clipboard.writeText(user.public_id);
      setCopyToast('success');
    } catch {
      setCopyToast('error');
    } finally {
      setCopyBusy(false);
      copyTimerRef.current = window.setTimeout(() => setCopyToast(null), 2400);
    }
  }

  function displayDate(iso: string) {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
    const date = new Date(iso + 'T12:00:00');
    return Number.isNaN(date.getTime()) ? iso : new Intl.DateTimeFormat(formatLocaleTag(locale), {
      day: '2-digit', month: 'short', year: 'numeric',
    }).format(date);
  }

  function proposedPatch(): Partial<Record<IdentityField, string>> {
    const patch: Partial<Record<IdentityField, string>> = {};
    if (!locked.has('given_name')) patch.given_name = givenName.trim();
    if (!locked.has('family_name')) patch.family_name = familyName.trim();
    if (!locked.has('date_of_birth')) patch.date_of_birth = dob;
    if (!locked.has('gender')) patch.gender = gender;
    return patch;
  }

  function validateAndConfirm() {
    const patch = proposedPatch();
    const entries = Object.entries(patch);
    if (!entries.length) return;
    if (entries.some(([, value]) => !value) || (!locked.has('gender') && !['male', 'female'].includes(gender))) {
      setFeedback(msg.missing);
      return;
    }
    setFeedback('');
    setSaveConfirm(true);
  }

  async function confirmSave() {
    const patch = proposedPatch();
    if (busy || !Object.keys(patch).length) return;
    setBusy(true);
    setFeedback('');
    try {
      const next = await updateProfile(patch);
      applyUser(next);
      setSaveConfirm(false);
      setFeedback(msg.saved);
    } catch (err) {
      setSaveConfirm(false);
      setFeedback(errorDetail(err) === 'identity_field_locked' ? msg.alreadySaved : msg.failed);
    } finally {
      setBusy(false);
    }
  }



  if (loading) return <div className="page"><div className="skeleton" style={{ minHeight: 240 }} /></div>;
  if (!user) return <div className="page"><div className="vbb-pf-login"><h1>{msg.title}</h1><Link className="btn btn-primary" to="/auth/login">{t('common:login', { defaultValue: 'Login' })}</Link></div></div>;

  const displayName = (user.given_name ? [user.given_name, user.family_name].filter(Boolean).join(' ') : '')
    || (user.display_name && !user.display_name.includes('@') ? user.display_name : '')
    || (user.username && !user.username.includes('@') ? user.username : '')
    || 'VBB PLAY';
  const contacts = [
    {
      key: 'email',
      label: msg.email,
      icon: <MailMark size={20} />,
      value: user.email || msg.notLinked,
      status: user.email ? (user.email_verified ? msg.linked : msg.unverified) : msg.notLinked,
      connected: Boolean(user.email && user.email_verified),
    },
    {
      key: 'phone',
      label: msg.phone,
      icon: <PhoneMark size={20} />,
      value: user.phone || msg.notLinked,
      status: user.phone ? (user.phone_verified ? msg.linked : msg.unverified) : msg.notLinked,
      connected: Boolean(user.phone && user.phone_verified),
    },
    {
      key: 'telegram',
      label: msg.telegram,
      icon: <TelegramMark size={20} />,
      value: user.username && user.telegram_id ? `@${user.username}` : user.telegram_id ? msg.linked : msg.notLinked,
      status: user.telegram_id ? msg.linked : msg.notLinked,
      connected: Boolean(user.telegram_id),
    },
  ];

  return (
    <div className="page vbb-pf-page">
      {copyToast ? <div className={`vbb-pf-copy-toast vbb-pf-copy-toast--${copyToast}`} role="status" aria-live="polite">
        <div className="vbb-pf-copy-toast-line"><span className="vbb-pf-copy-toast-icon" aria-hidden="true">{copyToast === 'success' ? '✓' : '!'}</span><span>{copyToast === 'success' ? msg.copied : msg.copyFailed}</span></div>
        <div className="vbb-pf-copy-toast-track" aria-hidden="true"><span key={copyToast} /></div>
      </div> : null}

      <button type="button" className="vbb-pf-identity" onClick={() => void copyId()} aria-label={msg.idCopy} disabled={copyBusy}>
        <img src="/vbb-fox-avatar.png" className="vbb-pf-avatar" alt="VBB PLAY" />
        <span className="vbb-pf-id-info"><strong>{displayName}</strong>
          <small>{msg.idCopy} <svg className="vbb-pf-copy-icon" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="8" y="8" width="13" height="13" rx="2"/><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3"/></svg></small>
        </span>
        {copyBusy ? <span className="vbb-pf-inline-spinner" aria-hidden="true"/> : null}
      </button>

      <section className="vbb-pf-section vbb-pf-details-section">
        <h2>{msg.info} {allLocked ? <span className="vbb-pf-verified" aria-label={msg.alreadySaved}>✓</span> : null}</h2>
        <form className="vbb-pf-form" onSubmit={(e) => { e.preventDefault(); validateAndConfirm(); }}>
          <div className="vbb-pf-fields">
            <label className="vbb-pf-field"><span>{msg.name}</span>{locked.has('given_name') ? <strong className="vbb-pf-static">{givenName || '—'}</strong> : <input aria-label={msg.name} placeholder={msg.name} value={givenName} onChange={(e) => setGivenName(e.target.value)} maxLength={80} />}</label>
            <label className="vbb-pf-field"><span>{msg.surname}</span>{locked.has('family_name') ? <strong className="vbb-pf-static">{familyName || '—'}</strong> : <input aria-label={msg.surname} placeholder={msg.surname} value={familyName} onChange={(e) => setFamilyName(e.target.value)} maxLength={80} />}</label>
            <label className="vbb-pf-field"><span>{msg.birth}</span>{locked.has('date_of_birth') ? <strong className="vbb-pf-static">{displayDate(dob) || '—'}</strong> : <input aria-label={msg.birth} type="date" value={dob} onChange={(e) => setDob(e.target.value)} />}</label>
            <label className="vbb-pf-field"><span>{msg.gender}</span>
              {locked.has('gender') ? <strong className="vbb-pf-static">{gender === 'male' ? msg.male : gender === 'female' ? msg.female : (gender || '—')}</strong> :
                <select aria-label={msg.gender} value={gender} onChange={(e) => setGender(e.target.value)}>
                  <option value="" disabled>{msg.chooseGender}</option>
                  <option value="male">{msg.male}</option><option value="female">{msg.female}</option>
                </select>}
            </label>
          </div>
          {anyUnlocked ? <button type="submit" className="vbb-pf-save" disabled={busy}>{msg.save}</button> : null}
        </form>
        {feedback ? <p className="vbb-pf-feedback" role="status">{feedback}</p> : null}
      </section>

      <section className="vbb-pf-section vbb-pf-contacts-section">
        <h2>{msg.contacts}</h2>
        <div className="vbb-pf-contacts">
          {contacts.map((contact) => (
            <Link className="vbb-pf-contact" key={contact.key} to="/security">
              <span className={`vbb-pf-contact-icon${contact.key === 'telegram' ? ' vbb-pf-contact-icon--telegram' : ''}`}>{contact.icon}</span>
              <div className="vbb-pf-contact-content">
                <strong>{contact.label}</strong>
                <small>{contact.value}</small>
                {contact.connected ? (
                  <span className="vbb-pf-status-pill"><span className="vbb-pf-connected">✓</span>{contact.status}</span>
                ) : (
                  <span className="vbb-pf-status-muted">{contact.status}</span>
                )}
              </div>
              <span className="vbb-pf-contact-action">{msg.manage}</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="vbb-pf-section vbb-pf-logout-section">
        <button type="button" className="vbb-pf-logout" disabled={loggingOut} onClick={() => { setLoggingOut(true); void logout().then(() => navigate('/auth/login', { replace: true })).finally(() => setLoggingOut(false)); }}>
          <span aria-hidden="true">↪</span> {loggingOut ? msg.loggingOut : msg.logout}
        </button>
      </section>
      <ProfileRichFooter />

      {saveConfirm ? <div className="vbb-pf-overlay" role="presentation">
        <div className="vbb-pf-dialog" role="dialog" aria-modal="true" aria-labelledby="vbb-pf-confirm-title">
          <h3 id="vbb-pf-confirm-title">{msg.confirmTitle}</h3><p>{msg.confirmText}</p>
          <button type="button" className="vbb-pf-save" onClick={() => void confirmSave()} disabled={busy}>{msg.verify}</button>
          <button type="button" className="vbb-pf-cancel" onClick={() => setSaveConfirm(false)} disabled={busy}>{msg.back}</button>
        </div>
      </div> : null}
    </div>
  );
}
